import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import {
  createPublicTestLink,
  listPublicTestLinks,
  publicTestLinkUrl,
  revokePublicTestLink,
  type PublicTestLink,
} from "@/services/publicTestLinkService";
import { Copy, Link2, Loader2, Share2 } from "lucide-react";
import { useCallback, useState } from "react";

const EXPIRY_OPTIONS = [
  { label: "1 hour", hours: 1 },
  { label: "24 hours", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
  { label: "30 days", hours: 24 * 30 },
];

const STATUS_VARIANT: Record<
  PublicTestLink["status"],
  "default" | "secondary" | "destructive"
> = {
  active: "default",
  expired: "secondary",
  revoked: "destructive",
};

/**
 * Lets an organization member share the assistant's test session through a link that works
 * without a Voxi login, and revoke links they no longer want live. Needs a saved assistant.
 */
export default function ShareTestLinkDialog({
  assistantId,
}: {
  assistantId?: string;
}) {
  const [links, setLinks] = useState<PublicTestLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiryHours, setExpiryHours] = useState("24");
  const [label, setLabel] = useState("");

  const load = useCallback(async () => {
    if (!assistantId) return;
    setLoading(true);
    try {
      setLinks(await listPublicTestLinks(assistantId));
    } catch (error: any) {
      toast({
        title: "Could not load links",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [assistantId]);

  const copy = async (link: PublicTestLink) => {
    try {
      await navigator.clipboard.writeText(publicTestLinkUrl(link.token));
      toast({ title: "Link copied" });
    } catch {
      toast({
        title: "Could not copy",
        description: publicTestLinkUrl(link.token),
        variant: "destructive",
      });
    }
  };

  const create = async () => {
    if (!assistantId) return;
    setCreating(true);
    try {
      const link = await createPublicTestLink({
        assistantId,
        expiresInHours: Number(expiryHours),
        label: label.trim() || undefined,
      });
      setLinks((prev) => [link, ...prev]);
      setLabel("");
      await copy(link);
    } catch (error: any) {
      toast({
        title: "Could not create link",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (link: PublicTestLink) => {
    try {
      const revoked = await revokePublicTestLink(link.id);
      setLinks((prev) => prev.map((l) => (l.id === link.id ? revoked : l)));
      toast({ title: "Link revoked" });
    } catch (error: any) {
      toast({
        title: "Could not revoke link",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog onOpenChange={(open) => open && load()}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" disabled={!assistantId}>
          <Share2 className="mr-2 h-4 w-4" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Share test link</DialogTitle>
          <DialogDescription>
            Anyone with a link can talk to this assistant in their browser
            without a Voxi account, until it expires or you revoke it. Each
            session is a real call and uses your credits.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="Note, e.g. who it is for (optional)"
            value={label}
            maxLength={100}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Select value={expiryHours} onValueChange={setExpiryHours}>
            <SelectTrigger className="sm:w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRY_OPTIONS.map((option) => (
                <SelectItem key={option.hours} value={String(option.hours)}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" onClick={create} disabled={creating}>
            {creating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Link2 className="mr-2 h-4 w-4" />
                Create
              </>
            )}
          </Button>
        </div>

        <div className="max-h-72 space-y-2 overflow-y-auto">
          {loading && (
            <p className="text-sm text-muted-foreground">Loading links...</p>
          )}
          {!loading && links.length === 0 && (
            <p className="text-sm text-muted-foreground">No links yet.</p>
          )}
          {links.map((link) => (
            <div
              key={link.id}
              className="flex items-center justify-between gap-2 rounded-md border p-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant={STATUS_VARIANT[link.status]}>
                    {link.status}
                  </Badge>
                  <span className="truncate text-sm">
                    {link.label || "Untitled link"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {link.status === "active" ? "Expires" : "Expired"}{" "}
                  {new Date(link.expires_at).toLocaleString()} ·{" "}
                  {link.session_count}{" "}
                  {link.session_count === 1 ? "session" : "sessions"}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                {link.status === "active" && (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copy(link)}
                    >
                      <Copy className="mr-1 h-3 w-3" />
                      Copy
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="text-destructive"
                      onClick={() => revoke(link)}
                    >
                      Revoke
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
